import { PIN_RE } from '@mm/shared';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Lock } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/display';
import { Input } from '../../components/ui/inputs';
import { useAuth } from './AuthProvider';

const passkeyError = (err: unknown) =>
  err instanceof ApiError
    ? err.message
    : err instanceof Error && err.name === 'NotAllowedError'
      ? 'Fingerprint / Face ID was cancelled.'
      : 'Fingerprint / Face ID is not available on this device.';

/** Full-screen lock: the app stays loaded underneath; PIN, passkey or password unlocks it. */
export function LockScreen() {
  const { lockedUser, unlock, unlockWithPasskey, logout } = useAuth();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'pin' | 'password'>(
    lockedUser?.hasPin === false ? 'password' : 'pin',
  );
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canPasskey = !!lockedUser?.hasPasskey && browserSupportsWebAuthn();

  const done = () => void queryClient.invalidateQueries();
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      done();
    } catch (err) {
      setError(passkeyError(err));
      setValue('');
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'pin' && !PIN_RE.test(value)) return setError('Enter your 4–6 digit PIN');
    if (!value) return;
    void run(() => unlock(mode === 'pin' ? { pin: value } : { password: value }));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="App locked"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/95 px-4 backdrop-blur"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-xs space-y-4 rounded-2xl bg-white p-6 text-center shadow-xl"
      >
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-brand-100 text-brand-700">
          <Lock className="size-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Locked</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {lockedUser?.name ? `${lockedUser.name} · ` : ''}
            {mode === 'pin' ? 'Enter your PIN to continue' : 'Enter your password to continue'}
          </p>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
        <Input
          aria-label={mode === 'pin' ? 'PIN' : 'Password'}
          type="password"
          inputMode={mode === 'pin' ? 'numeric' : undefined}
          autoComplete={mode === 'pin' ? 'off' : 'current-password'}
          maxLength={mode === 'pin' ? 6 : 128}
          autoFocus
          value={value}
          onChange={(e) =>
            setValue(mode === 'pin' ? e.target.value.replace(/\D/g, '') : e.target.value)
          }
          className={mode === 'pin' ? 'text-center text-2xl tracking-[0.5em]' : ''}
        />
        <Button type="submit" className="w-full" loading={busy}>
          Unlock
        </Button>
        {canPasskey && (
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            icon={<Fingerprint className="size-4" />}
            disabled={busy}
            onClick={() => void run(unlockWithPasskey)}
          >
            Use fingerprint / Face ID
          </Button>
        )}
        <div className="flex justify-between text-xs">
          {lockedUser?.hasPin !== false && (
            <button
              type="button"
              className="text-brand-700 hover:underline"
              onClick={() => {
                setMode(mode === 'pin' ? 'password' : 'pin');
                setValue('');
                setError(null);
              }}
            >
              {mode === 'pin' ? 'Use password instead' : 'Use PIN instead'}
            </button>
          )}
          <button
            type="button"
            className="ml-auto text-slate-500 hover:text-rose-600 hover:underline"
            onClick={() => void logout()}
          >
            Sign out
          </button>
        </div>
      </form>
    </div>
  );
}
